import { useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

/** Multi-select panel picker with search, used when linking a task to panels. */
export function PanelPicker({ panels, value, onChange }) {
  const [query, setQuery] = useState("");
  const selected = value || [];

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = needle
      ? panels.filter((p) => (p.panel_name || "").toLowerCase().includes(needle))
      : panels;
    return rows.slice(0, 200);
  }, [panels, query]);

  const toggle = (uid) =>
    onChange(selected.includes(uid) ? selected.filter((s) => s !== uid) : [...selected, uid]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="task-panel-select"
          className="flex h-9 w-full items-center justify-between gap-1 rounded-md border border-slate-200 bg-[#EEF2F8] px-3 text-xs text-slate-700 hover:bg-slate-100"
        >
          <span className="truncate">
            {selected.length === 0 ? "No panels linked" : `${selected.length} panel(s)`}
          </span>
          <ChevronDown size={13} className="shrink-0 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64 border-slate-200 bg-white p-0 text-slate-800"
      >
        <div className="flex items-center gap-1.5 border-b border-slate-100 px-2.5 py-2">
          <Search size={12} className="text-slate-400" />
          <Input
            data-testid="task-panel-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search panels"
            className="h-7 border-0 bg-transparent px-0 text-xs focus-visible:ring-0"
          />
        </div>
        <div className="okn-scroll max-h-56 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">No panel matches.</p>
          ) : (
            filtered.map((panel) => (
              <button
                key={panel.panel_uid}
                data-testid={`task-panel-option-${panel.panel_uid}`}
                onClick={() => toggle(panel.panel_uid)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-slate-100"
              >
                <span
                  className={`grid h-3.5 w-3.5 shrink-0 place-items-center rounded border ${
                    selected.includes(panel.panel_uid)
                      ? "border-cyan-600 bg-cyan-600 text-white"
                      : "border-slate-300"
                  }`}
                >
                  {selected.includes(panel.panel_uid) ? "✓" : ""}
                </span>
                <span className="truncate">{panel.panel_name || panel.panel_uid}</span>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
